// Account page — credit balance + Stripe customer portal entry +
// recent top-up history + CTAs to buy more / manage subscription.
//
// This is the destination of Stripe Checkout's success_url
// (`/account?topup=success&session_id=...` and the equivalent for
// subscriptions), so we surface a confirmation toast and trigger a
// balance refetch so the chip catches up to the webhook grant.

import { useEffect, useRef, useState } from 'react';
import Icon from '../../../packages/ui/Icon';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { creditsToMinutes } from '../lib/cost';
import { getToken } from '../lib/auth';
import { useBillingLive } from '../lib/billing';
import { LegalFooter } from '../components/LegalFooter';
import { TopBar } from '../components/TopBar';
import { useT } from '../lib/i18n';
import './Account.css';

interface TopupRow {
  id: string;
  package_id: string;
  amount_paid_cents: number;
  credits_added: number;
  status: 'pending' | 'succeeded' | 'failed' | string;
  created_at: string;
  completed_at: string | null;
}

interface BalanceResponse {
  credits_balance: number;
  plan: string;
  is_admin: boolean;
  recent_topups: TopupRow[];
}

interface QuotaResponse {
  plan: string;
  is_admin: boolean;
  compile_concurrency: { limit: number; active: number; available: number };
  daily:   { limit: number; used: number; remaining: number };
  monthly: { limit: number; used: number; remaining: number };
  storage: { limit_bytes: number; used_bytes: number; remaining_bytes: number };
  blocked: boolean;
  blocking_window: 'daily' | 'monthly' | null;
}

const PLAN_LABEL: Record<string, string> = {
  free:     'Free',
  basic:    'Basic',
  pro:      'Pro',
  ultimate: 'Ultimate',
  max:      'Max',
};

// Quota rows in minutes (credits are internal-only since 2026-06-12).
// A beta/no-op limit (≥10^8 credits) renders as ∞ rather than a
// nonsense hundred-million-minute cap.
function fmtQuotaMinutes(usedCredits: number, limitCredits: number): string {
  const used = creditsToMinutes(usedCredits).toLocaleString();
  if (limitCredits >= 1e8) return `${used} / ∞ min`;
  return `${used} / ${creditsToMinutes(limitCredits).toLocaleString()} min`;
}

function fmtUsd(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function fmtDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

function fmtBytes(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = Math.max(0, bytes);
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const digits = unit === 0 || value >= 10 ? 0 : 1;
  return `${value.toFixed(digits)} ${units[unit]}`;
}

export function Account() {
  const t = useT();
  const qc = useQueryClient();
  const [search, setSearch] = useSearchParams();
  const [toast, setToast] = useState<string | null>(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);

  const balanceQ = useQuery({
    queryKey: ['me', 'balance'],
    queryFn: () => api<BalanceResponse>('/api/v2/me/balance'),
    refetchInterval: 30_000,
  });

  const quotaQ = useQuery({
    queryKey: ['me', 'quota'],
    queryFn: () => api<QuotaResponse>('/api/v2/me/quota'),
    refetchInterval: 30_000,
  });

  // Same source of truth as Pricing — when false, hide subscription-
  // management UI (Stripe Customer Portal would 404). See lib/billing.ts.
  const billingLive = useBillingLive();

  // Returning from Stripe Checkout — show a toast + nudge a refetch so the
  // balance catches up to the webhook-side grant. Webhook is async so the
  // balance might still show old value for a few seconds; the 30s poll
  // will catch up regardless.
  useEffect(() => {
    const checkout = search.get('checkout');
    const topup = search.get('topup');
    let toast: string | null = null;
    if (checkout === 'success') {
      toast = '🎉 Subscription active — credits granted.';
      qc.invalidateQueries({ queryKey: ['me'] });
    } else if (topup === 'success') {
      toast = '🎉 Top-up successful — credits added.';
      qc.invalidateQueries({ queryKey: ['me'] });
    } else if (checkout === 'cancelled' || topup === 'cancelled') {
      toast = 'Checkout cancelled. No charge was made.';
    }
    if (toast) queueMicrotask(() => setToast(toast));
    if (checkout || topup) {
      const next = new URLSearchParams(search);
      next.delete('checkout');
      next.delete('topup');
      next.delete('session_id');
      queueMicrotask(() => setSearch(next, { replace: true }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function openPortal() {
    setPortalBusy(true);
    setPortalError(null);
    try {
      const r = await api<{ url: string }>('/api/v2/billing/portal', { method: 'POST' });
      window.location.href = r.url;
    } catch (e) {
      let msg: string;
      if (e instanceof ApiError) {
        try {
          const parsed = JSON.parse(e.body) as { detail?: string };
          msg = parsed.detail || e.body || `error ${e.status}`;
        } catch {
          msg = e.body || `error ${e.status}`;
        }
      } else {
        msg = e instanceof Error ? e.message : String(e);
      }
      setPortalError(msg);
      setPortalBusy(false);
    }
  }

  const balance = balanceQ.data;
  const quota = quotaQ.data;
  const plan = balance?.plan || 'free';
  const planLabel = PLAN_LABEL[plan] || plan;
  // Show "Manage subscription" only when billing is live AND the user
  // has actually transacted (otherwise Stripe Customer Portal would 404).
  const hasStripeCustomer = billingLive && (
    !!balance?.recent_topups.length || plan !== 'free'
  );
  const isLowBalance = (balance?.credits_balance ?? 0) < 100 && plan === 'free';

  return (
    <div className="account-page">
      <TopBar
        links={
          <>
            <Link to="/download">{t('nav.download')}</Link>
            <Link to="/pricing">{t('nav.pricing')}</Link>
          </>
        }
      />

      {toast && (
        <div className="account-toast" onClick={() => setToast(null)}>
          {toast}
        </div>
      )}
      {portalError && (
        <div className="account-toast error" onClick={() => setPortalError(null)}>
          {portalError}
        </div>
      )}

      <main className="account-shell">
        <h1>{t('account.heading')}</h1>

        {!billingLive && (
          <div className="account-beta-banner">
            {t('account.beta_lead')}<strong>{t('account.beta_strong')}</strong>{t('account.beta_tail')}
          </div>
        )}

        <section className="account-card">
          <div className="account-balance">
            <div className="account-balance-amount">
              {creditsToMinutes(balance?.credits_balance ?? 0).toLocaleString()} min
            </div>
            <div className="account-balance-meta">
              {balanceQ.isLoading ? t('account.balance_loading') : t('account.balance_label')}
              {balance?.is_admin && <span className="pill">admin</span>}
            </div>
          </div>
          <div className="account-balance-actions">
            <Link to="/pricing" className="btn btn-primary">{t('account.btn.buy_more')}</Link>
            {hasStripeCustomer && (
              <button
                className="btn"
                onClick={openPortal}
                disabled={portalBusy}
                title={t('account.btn.manage_tooltip')}
              >
                {portalBusy ? t('account.btn.manage_opening') : t('account.btn.manage_sub')}
              </button>
            )}
          </div>
          {isLowBalance && (
            <p className="account-low-balance">
              {t('account.low_balance')}<code>insufficient_credits</code>{t('account.low_balance_end')}
            </p>
          )}
        </section>

        <section className="account-grid">
          <div className="account-card">
            <h3>{t('account.plan_heading')}</h3>
            <div className="account-plan-name">{planLabel}</div>
            {quota && (
              <table className="account-table">
                <tbody>
                  <tr>
                    <td>{t('account.plan.daily')}</td>
                    <td className="num">
                      {fmtQuotaMinutes(quota.daily.used, quota.daily.limit)}
                    </td>
                  </tr>
                  <tr>
                    <td>{t('account.plan.monthly')}</td>
                    <td className="num">
                      {fmtQuotaMinutes(quota.monthly.used, quota.monthly.limit)}
                    </td>
                  </tr>
                  <tr>
                    <td>{t('account.plan.concurrent')}</td>
                    <td className="num">
                      {quota.compile_concurrency.active} / {quota.compile_concurrency.limit}
                    </td>
                  </tr>
                  <tr>
                    <td>{t('account.plan.storage')}</td>
                    <td className="num">
                      {fmtBytes(quota.storage.used_bytes)} / {fmtBytes(quota.storage.limit_bytes)}
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
            {plan === 'free' && (
              <p className="muted small">{t('account.plan.free_note')}</p>
            )}
            <Link to="/pricing" className="account-link-row">
              {t('account.plan.compare')}
            </Link>
          </div>

          <div className="account-card">
            <h3>{t('account.activity_heading')}</h3>
            {!balance || balance.recent_topups.length === 0 ? (
              <p className="muted small">
                {t('account.activity.empty')} <Link to="/pricing">{t('account.activity.empty_link')}</Link>
              </p>
            ) : (
              <table className="account-table account-topups">
                <thead>
                  <tr>
                    <th>{t('account.activity.col_when')}</th>
                    <th>{t('account.activity.col_pkg')}</th>
                    <th className="num">{t('account.activity.col_paid')}</th>
                    <th className="num">{t('account.activity.col_credits')}</th>
                    <th>{t('account.activity.col_status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {balance.recent_topups.map(row => (
                    <tr key={row.id} className={`status-${row.status}`}>
                      <td className="muted small">
                        {fmtDate(row.completed_at || row.created_at)}
                      </td>
                      <td>{row.package_id}</td>
                      <td className="num">{fmtUsd(row.amount_paid_cents)}</td>
                      <td className="num">+{creditsToMinutes(row.credits_added).toLocaleString()} min</td>
                      <td>
                        <span className={`pill status-${row.status}`}>
                          {row.status === 'succeeded' ? '✓' : row.status === 'pending' ? '…' : '✗'}
                          {' '}{row.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        <ApiKeysSection />

        <VoiceCloneSection />

        <p className="account-foot muted small">
          {t('account.foot')}{' '}
          <Link to="/terms">{t('nav.terms')}</Link> · <Link to="/privacy">{t('nav.privacy')}</Link>
        </p>
      </main>

      <LegalFooter />
    </div>
  );
}

// ─── Voice clone section ────────────────────────────────────────────
// Plan A — qwen3-tts CustomVoice voice-clone registration. Local-only
// (no external API), no cost. User uploads ~30s clean speech sample +
// optional reference text; subsequent `\say[voice=mine]` calls in any
// project use voice_clone TTS keyed off this sample.
//
// Single-sample-per-user model. Re-uploading replaces the previous one.
// Delete reverts to default CustomVoice speakers.

interface VoiceSampleStatus {
  registered: boolean;
  uploaded_at?: string | null;
  ref_text?: string | null;
  size_bytes?: number;
}

function VoiceCloneSection() {
  const t = useT();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [refText, setRefText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);

  const statusQ = useQuery({
    queryKey: ['me', 'voice-sample'],
    queryFn: () => api<VoiceSampleStatus>('/api/v2/me/voice-sample'),
  });
  const status = statusQ.data;

  const uploadMut = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      if (refText.trim()) fd.append('ref_text', refText.trim());
      // The api() wrapper auto-skips Content-Type when body is FormData,
      // so the browser fills in `multipart/form-data; boundary=...`.
      // We bypass it here only because the helper assumes JSON for body
      // serialization — using fetch directly with the auth header is safer.
      const r = await fetch('/api/v2/me/voice-sample', {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      if (!r.ok) {
        let msg = `HTTP ${r.status}`;
        try {
          const j = await r.json();
          msg = j.detail || msg;
        } catch { /* keep msg */ }
        throw new Error(msg);
      }
      return r.json() as Promise<{ ok: boolean; size_bytes: number; filename: string }>;
    },
    onSuccess: () => {
      setError(null);
      setHint(t('account.voice.success'));
      qc.invalidateQueries({ queryKey: ['me', 'voice-sample'] });
      if (fileRef.current) fileRef.current.value = '';
    },
    onError: (e) => {
      setHint(null);
      setError(e instanceof Error ? e.message : String(e));
    },
  });

  const deleteMut = useMutation({
    mutationFn: () => api(`/api/v2/me/voice-sample`, { method: 'DELETE' }),
    onSuccess: () => {
      setError(null);
      setHint(t('account.voice.deleted'));
      qc.invalidateQueries({ queryKey: ['me', 'voice-sample'] });
    },
    onError: (e) => {
      setError(e instanceof Error ? e.message : String(e));
    },
  });

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size < 50 * 1024) {
      setError(t('account.voice.too_small').replace('{size}', (f.size / 1024).toFixed(1)));
      return;
    }
    if (f.size > 50 * 1024 * 1024) {
      setError(t('account.voice.too_large').replace('{size}', (f.size / 1024 / 1024).toFixed(1)));
      return;
    }
    uploadMut.mutate(f);
  }

  return (
    <section className="account-card">
      <h3><Icon name="mic" size={15} /> {t('account.voice.heading')}</h3>
      <p className="muted small" style={{ marginTop: 0 }}>
        {t('account.voice.intro_l')}
        <code>{'\\say[voice=mine]{...}'}</code>
        {t('account.voice.intro_r')}
      </p>

      {statusQ.isLoading && <div className="muted">{t('account.voice.loading')}</div>}

      {status && status.registered && (
        <div className="voice-clone-registered">
          <div>
            <strong>{t('account.voice.registered')}</strong>
            {status.size_bytes != null && (
              <span className="muted small">
                {' '}· {(status.size_bytes / 1024).toFixed(0)} KB
              </span>
            )}
            {status.uploaded_at && (
              <span className="muted small">
                {' '}· {fmtDate(status.uploaded_at)}
              </span>
            )}
          </div>
          {status.ref_text && (
            <div className="muted small voice-clone-ref">
              {t('account.voice.ref_label')}"{status.ref_text}"
            </div>
          )}
          <div className="voice-clone-actions">
            <button
              className="btn btn-danger"
              onClick={() => {
                if (confirm(t('account.voice.delete_confirm'))) deleteMut.mutate();
              }}
              disabled={deleteMut.isPending}
            >
              {deleteMut.isPending ? t('account.voice.delete_busy') : t('account.voice.delete_btn')}
            </button>
          </div>
        </div>
      )}

      {status && !status.registered && (
        <div className="voice-clone-form">
          <label className="muted small" htmlFor="voice-ref-text">
            {t('account.voice.ref_text_label')}
          </label>
          <input
            id="voice-ref-text"
            type="text"
            placeholder={t('account.voice.ref_text_ph')}
            value={refText}
            onChange={(e) => setRefText(e.target.value)}
            disabled={uploadMut.isPending}
          />
          <input
            ref={fileRef}
            type="file"
            accept="audio/*,.wav,.mp3,.m4a,.ogg,.flac"
            onChange={onPickFile}
            disabled={uploadMut.isPending}
            style={{ display: 'none' }}
          />
          <button
            className="btn btn-primary"
            onClick={() => fileRef.current?.click()}
            disabled={uploadMut.isPending}
          >
            <Icon name="folder" size={13} /> {uploadMut.isPending ? t('account.voice.upload_busy') : t('account.voice.upload_btn')}
          </button>
          <p className="muted small">{t('account.voice.upload_help')}</p>
        </div>
      )}

      {error && <div className="account-error">⚠ {error}</div>}
      {hint && <div className="account-hint">{hint}</div>}
    </section>
  );
}


// ─── API Keys section ───────────────────────────────────────────────
// Per-user API key for the `autolecture-python` SDK and any
// service-to-service / CLI use. Three operations:
//
//   - status: GET /me/api-key → {registered, last4, created_at}
//   - mint:   POST /me/api-key → {api_key, created_at, warning} — the
//             ONLY place the full secret appears
//   - revoke: DELETE /me/api-key → 204
//
// One key per user (rotate replaces, doesn't append). The secret is
// surfaced in a panel with a Copy button + an explicit "store now,
// never shown again" warning. After dismissal we return to status
// view (only last4 + created_at visible).

interface ApiKeyStatus {
  registered: boolean;
  last4: string | null;
  created_at: string | null;
}

interface ApiKeyMintResult {
  api_key: string;
  created_at: string;
  warning: string;
}

function ApiKeysSection() {
  const t = useT();
  const qc = useQueryClient();
  const [secret, setSecret] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const statusQ = useQuery({
    queryKey: ['me', 'api-key'],
    queryFn: () => api<ApiKeyStatus>('/api/v2/me/api-key'),
  });
  const status = statusQ.data;

  const mintMut = useMutation({
    mutationFn: () => api<ApiKeyMintResult>('/api/v2/me/api-key', { method: 'POST' }),
    onSuccess: (r) => {
      setError(null);
      setCopied(false);
      setSecret(r.api_key);
      qc.invalidateQueries({ queryKey: ['me', 'api-key'] });
    },
    onError: (e) => {
      setError(e instanceof Error ? e.message : String(e));
    },
  });

  const revokeMut = useMutation({
    mutationFn: () => api('/api/v2/me/api-key', { method: 'DELETE' }),
    onSuccess: () => {
      setError(null);
      setSecret(null);
      qc.invalidateQueries({ queryKey: ['me', 'api-key'] });
    },
    onError: (e) => {
      setError(e instanceof Error ? e.message : String(e));
    },
  });

  async function copySecret() {
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API blocked (insecure context, denied perms). Fall
      // back to a no-op: the textarea is selectable, user can Cmd-A.
    }
  }

  return (
    <section className="account-card">
      <h3>{t('account.apikey.heading')}</h3>
      <p className="muted small" style={{ marginTop: 0 }}>
        {t('account.apikey.intro_l')}
        <a href="https://github.com/scao7/autolecture-python" target="_blank" rel="noreferrer">
          autolecture-python
        </a>
        {t('account.apikey.intro_r')}
      </p>

      {statusQ.isLoading && <div className="muted">{t('account.apikey.loading')}</div>}

      {secret && (
        // One-shot reveal panel — appears only after a successful mint.
        // After the user dismisses, the secret is gone from React state
        // and unrecoverable; they'd have to rotate to get a new one.
        <div className="apikey-reveal">
          <div className="apikey-reveal-warning">
            ⚠ {mintMut.data?.warning || t('account.apikey.warning_default')}
          </div>
          <div className="apikey-reveal-row">
            <input
              className="apikey-reveal-input"
              type="text"
              readOnly
              value={secret}
              onClick={(e) => (e.target as HTMLInputElement).select()}
            />
            <button className="btn" onClick={copySecret}>
              {copied ? t('account.apikey.copied') : t('account.apikey.copy')}
            </button>
          </div>
          <div className="apikey-reveal-actions">
            <button
              className="btn btn-primary"
              onClick={() => setSecret(null)}
            >
              {t('account.apikey.dismiss')}
            </button>
          </div>
        </div>
      )}

      {!secret && status && status.registered && (
        <div className="apikey-status">
          <div>
            <strong>{t('account.apikey.registered')}</strong>{' '}
            <code className="apikey-last4">·{status.last4}</code>
            {status.created_at && (
              <span className="muted small">
                {' '}· {t('account.apikey.minted_at')} {fmtDate(status.created_at)}
              </span>
            )}
          </div>
          <div className="apikey-actions">
            <button
              className="btn"
              onClick={() => {
                if (confirm(t('account.apikey.rotate_confirm'))) mintMut.mutate();
              }}
              disabled={mintMut.isPending}
            >
              {mintMut.isPending ? t('account.apikey.minting') : t('account.apikey.rotate')}
            </button>
            <button
              className="btn btn-danger"
              onClick={() => {
                if (confirm(t('account.apikey.revoke_confirm'))) revokeMut.mutate();
              }}
              disabled={revokeMut.isPending}
            >
              {revokeMut.isPending ? t('account.apikey.revoking') : t('account.apikey.revoke')}
            </button>
          </div>
        </div>
      )}

      {!secret && status && !status.registered && (
        <div className="apikey-empty">
          <p className="muted small">{t('account.apikey.empty_help')}</p>
          <button
            className="btn btn-primary"
            onClick={() => mintMut.mutate()}
            disabled={mintMut.isPending}
          >
            {mintMut.isPending ? t('account.apikey.minting') : t('account.apikey.mint')}
          </button>
        </div>
      )}

      {error && <div className="account-error">⚠ {error}</div>}
    </section>
  );
}
